import { Info, LayoutGrid, Lock, Maximize, Minimize, ShieldCheck, UserSquare2 } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { formatMeetingId } from '../../shared/protocol';
import { Button, Popover } from '../components/ui';
import { joinUrl } from '../lib/api';
import { formatClock } from '../lib/format';
import { canShareNatively, copyWithFeedback, shareLink } from '../lib/share';
import { inviteText } from '../lib/invite';
import { useSettings } from '../lib/settings';
import { getOwnedMeeting } from '../lib/storage';
import { useStore } from '../lib/store';
import type { MeetingSession } from '../meeting/MeetingSession';
import type { Quality } from '../meeting/Peer';
import type { ViewMode } from './Stage';

export function meetingPasscode(session: MeetingSession): string | null {
  const id = session.store.get().meeting?.id;
  if (!id) return null;
  return getOwnedMeeting(id)?.passcode ?? new URLSearchParams(location.search).get('pwd');
}

function useElapsed(since: number | null): string {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);
  return since ? formatClock(now - since) : '00:00';
}

const QUALITY_TEXT: Record<Quality, string> = { good: 'Conexión buena', fair: 'Conexión aceptable', poor: 'Conexión inestable', unknown: 'Midiendo la conexión…' };

export function TopBar({ session, view, onToggleView, fullscreen, onToggleFullscreen }: { session: MeetingSession; view: ViewMode; onToggleView(): void; fullscreen: boolean; onToggleFullscreen(): void }) {
  const s = useStore(session.store);
  const settings = useSettings();
  const [info, setInfo] = useState(false);
  const infoRef = useRef<HTMLButtonElement>(null);
  const elapsed = useElapsed(s.joinedAt);
  const meeting = s.meeting!;
  const host = Object.values(s.participants).find((p) => p.role === 'host');
  const passcode = meetingPasscode(session);

  // Worst link quality across peers drives the indicator.
  const stats = Object.values(s.peerStats);
  const order: Quality[] = ['poor', 'fair', 'good', 'unknown'];
  const quality: Quality = stats.length ? (order.find((q) => stats.some((st) => st.quality === q)) ?? 'unknown') : 'unknown';
  const worst = stats.filter((st) => st.rttMs !== null).sort((a, b) => (b.rttMs ?? 0) - (a.rttMs ?? 0))[0];
  const qualityTitle = stats.length
    ? `${QUALITY_TEXT[quality]}${worst?.rttMs != null ? ` · latencia ${Math.round(worst.rttMs)} ms · pérdida ${worst.lossPct?.toFixed(1) ?? 0}%` : ''}`
    : 'Estás solo en la reunión';

  return (
    <header className="topbar">
      <button ref={infoRef} type="button" className="topbar-btn" onClick={() => setInfo((v) => !v)} aria-label="Información de la reunión" aria-expanded={info}>
        <ShieldCheck size={16} style={{ color: 'var(--green)' }} />
        <Info size={16} />
      </button>
      <span className="topbar-title" title={meeting.title}>
        {meeting.title}
      </span>
      {(() => {
        // Everyone in the room sees who is recording (consent), like Zoom's red "Recording" label.
        const recorders = Object.values(s.participants).filter((p) => p.recording);
        if (!recorders.length) return null;
        const paused = recorders.every((p) => p.recording === 'paused');
        const who = recorders.map((p) => (p.id === s.selfId ? 'tú' : p.name)).join(', ');
        return (
          <span className={`rec-badge ${paused ? 'paused' : ''}`} role="status" title={`Graba: ${who}`}>
            <span className="rec-dot" aria-hidden />
            {paused ? 'Grabación en pausa' : 'Grabando'}
          </span>
        );
      })()}
      {s.locked && (
        <span className="badge" title="Reunión bloqueada">
          <Lock size={11} style={{ marginRight: 3 }} /> Bloqueada
        </span>
      )}
      <span className={`quality ${stats.length ? quality : ''}`} title={qualityTitle} role="img" aria-label={qualityTitle}>
        <span />
        <span />
        <span />
      </span>
      {settings.showTimer && <span className="timer" aria-label="Duración de la reunión">{elapsed}</span>}
      <div className="topbar-spacer" />
      <button type="button" className="topbar-btn hide-narrow" onClick={onToggleView} aria-label={`Cambiar a vista ${view === 'gallery' ? 'de orador' : 'de galería'}`}>
        {view === 'gallery' ? <UserSquare2 size={16} /> : <LayoutGrid size={16} />}
        {view === 'gallery' ? 'Vista de orador' : 'Vista de galería'}
      </button>
      <button type="button" className="topbar-btn" onClick={onToggleFullscreen} aria-label={fullscreen ? 'Salir de pantalla completa' : 'Pantalla completa'} title="Pantalla completa (Alt+F)">
        {fullscreen ? <Minimize size={16} /> : <Maximize size={16} />}
      </button>

      {info && (
        <Popover anchor={infoRef} onClose={() => setInfo(false)} placement="bottom" label="Información de la reunión">
          <div className="info-popover">
            <strong>{meeting.title}</strong>
            <dl>
              <dt>ID de reunión</dt>
              <dd>{formatMeetingId(meeting.id)}</dd>
              <dt>Anfitrión</dt>
              <dd>{host?.name ?? '—'}</dd>
              {passcode && (
                <>
                  <dt>Código</dt>
                  <dd>{passcode}</dd>
                </>
              )}
              <dt>Enlace</dt>
              <dd>{joinUrl(meeting.id, passcode)}</dd>
              <dt>Cifrado</dt>
              <dd>Medios cifrados de extremo a extremo entre navegadores (DTLS-SRTP)</dd>
            </dl>
            <div className="row">
              <Button
                size="sm"
                variant="primary"
                onClick={() =>
                  canShareNatively()
                    ? void shareLink({ title: meeting.title, text: `Únete a «${meeting.title}» en Nexo Meet`, url: joinUrl(meeting.id, passcode) })
                    : void copyWithFeedback(joinUrl(meeting.id, passcode), 'enlace')
                }
              >
                {canShareNatively() ? 'Compartir enlace' : 'Copiar enlace'}
              </Button>
              <Button
                size="sm"
                onClick={() => void copyWithFeedback(inviteText({ ...meeting, passcode }, host?.name), 'invitación')}
              >
                Copiar invitación
              </Button>
            </div>
          </div>
        </Popover>
      )}
    </header>
  );
}
